import { useCallback, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { atom, useAtom } from 'jotai';

import { DEFAULT_BPM } from '@/core/contants.ts';
import { createPlayer } from '@/core/AudioPlayer/AudioPlayer.ts';
import { Sequence, SIGNATURE } from '@/types.ts';

interface AudioPlayerState {
  isPlaying: boolean;
  isLoading: boolean;
  beat: number | undefined;
  bar: number | undefined;
  bpm: number;
}

const audioPlayerAtom = atom<AudioPlayerState>({
  beat: undefined,
  bar: undefined,
  bpm: DEFAULT_BPM,
  isPlaying: false,
  isLoading: false,
});

function useAudioPlayer() {
  const [state, setState] = useAtom(audioPlayerAtom);

  const { isLoading: isInitializing, data: player } = useQuery({
    queryKey: ['audioplayer'],
    queryFn: () => createPlayer({
      onLoadingChange: (isLoading) => setState((currentState) => ({ ...currentState, isLoading })),
      onBeatChange: (beat) => setState((currentState) => ({ ...currentState, beat })),
      onBarChange: (bar) => setState((currentState) => ({ ...currentState, bar })),
    }),
  });

  const start = useCallback((sequence: Sequence, signature: SIGNATURE) => {
    if (!player) return;

    setState((currentState) => ({
      ...currentState,
      isPlaying: true,
    }));

    player.start(sequence, signature);
  }, [player, setState]);

  const stop = useCallback(() => {
    if (!player) return;

    setState((currentState) => ({
      ...currentState,
      isPlaying: false,
    }));

    player.stop();
  }, [player, setState]);

  const setBpm = useCallback(
    (newBpm: number) => {
      player?.setBpm(newBpm);
      setState((currentState) => ({
        ...currentState,
        bpm: newBpm,
        isPlaying: false,
      }));
    },
    [setState, player],
  );

  return useMemo(() => ({
    ...state,
    isInitializing,
    start,
    stop,
    setBpm,
    player,
  }), [isInitializing, start, stop, player, state, setBpm]);
}

export default useAudioPlayer;
